import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, CheckCircle2, XCircle, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DecisionForm = ({ token, quote, onDone }) => {
  const [mode, setMode] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/public/quote/${token}/respond`, { decision: mode, name, note });
      onDone(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Please try again or contact us.");
      setBusy(false);
    }
  };
  if (!mode) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="quote-decision-card">
        <p className="font-semibold text-slate-800 text-center">Your decision</p>
        <p className="text-xs text-slate-500 text-center mt-1">{quote.expired ? "This quote has expired — you can still decline or contact us for an updated quote." : `This quote is valid until ${quote.valid_until}.`}</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button onClick={() => setMode("accept")} disabled={quote.expired} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white py-4 font-semibold flex items-center justify-center gap-2" data-testid="quote-accept-button"><CheckCircle2 className="w-5 h-5" /> Approve quote</button>
          <button onClick={() => setMode("decline")} className="rounded-xl border-2 border-red-300 text-red-700 hover:bg-red-50 py-4 font-semibold flex items-center justify-center gap-2" data-testid="quote-decline-button"><XCircle className="w-5 h-5" /> Decline</button>
        </div>
      </div>
    );
  }
  const accept = mode === "accept";
  return (
    <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="quote-decision-form">
      <p className={`font-semibold ${accept ? "text-emerald-700" : "text-red-700"}`}>{accept ? "Approve this quote" : "Decline this quote"}</p>
      <p className="text-xs text-slate-500 mt-1">{accept ? `By approving you authorize ${quote.company_name} to proceed with the work described above for ${money(quote.total)}.` : "Let us know what didn't fit — we're happy to revise."}</p>
      <label className="block mt-4 text-sm text-slate-700">Your name
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="quote-decision-name" />
      </label>
      <label className="block mt-3 text-sm text-slate-700">{accept ? "Comments (optional)" : "Reason (optional)"}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="quote-decision-note" />
      </label>
      {error && <p className="mt-3 text-sm text-red-600" data-testid="quote-decision-error">{error}</p>}
      <div className="flex gap-3 mt-4">
        <button onClick={() => setMode(null)} disabled={busy} className="flex-1 rounded-xl border border-slate-300 py-3 font-medium text-slate-700" data-testid="quote-decision-back">Back</button>
        <button onClick={submit} disabled={busy || !name.trim()} className={`flex-1 rounded-xl text-white py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 ${accept ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`} data-testid="quote-decision-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{accept ? "Confirm approval" : "Confirm decline"}
        </button>
      </div>
    </div>
  );
};

export default function ReviewQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    axios.get(`${API}/public/quote/${token}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "This quote link is not valid"));
  }, [token, refresh]);

  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6"><p className="text-slate-700 text-center" data-testid="quote-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" /></div>;
  const { company, quote: q } = data;
  const decided = q.customer_decision;
  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4" data-testid="quote-page">
      <div className="max-w-xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          {company.logo_b64 && <img src={`data:image/jpeg;base64,${company.logo_b64}`} alt={company.name} className="h-16 mx-auto mb-3 object-contain" />}
          <h1 className="text-xl font-bold text-slate-800" data-testid="quote-company-name">{company.name}</h1>
          <p className="text-xs text-slate-500">{company.address}{company.phone ? ` · ${company.phone}` : ""}</p>
        </div>

        <div className={`rounded-2xl shadow-lg p-6 text-white ${decided === "accepted" ? "bg-gradient-to-br from-emerald-600 to-green-700" : decided === "declined" ? "bg-gradient-to-br from-slate-600 to-slate-800" : q.expired ? "bg-gradient-to-br from-amber-600 to-orange-700" : "bg-gradient-to-br from-purple-700 to-indigo-800"}`} data-testid="quote-header-card">
          <div className="flex items-start justify-between">
            <div><p className="text-xs uppercase tracking-wide opacity-80">Quote</p><p className="text-2xl font-bold" data-testid="quote-number">{q.quote_number}</p><p className="text-sm opacity-90">{q.customer_name}</p></div>
            <div className="text-right"><p className="text-xs uppercase tracking-wide opacity-80">Total</p><p className="text-3xl font-bold" data-testid="quote-total">{money(q.total)}</p><p className="text-xs opacity-90">Valid until {q.valid_until}</p></div>
          </div>
          {decided && <p className="mt-4 flex items-center gap-2 text-sm font-medium" data-testid={`quote-decided-${decided}`}>{decided === "accepted" ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}{decided === "accepted" ? "Approved" : "Declined"} by {q.customer_decision_name} on {String(q.customer_decision_at || "").slice(0, 10)}{decided === "accepted" ? " — thank you! We'll be in touch to schedule the work." : ". Thank you for letting us know."}</p>}
          {!decided && q.expired && <p className="mt-4 flex items-center gap-2 text-sm font-medium"><AlertTriangle className="w-5 h-5" /> This quote expired on {q.valid_until}.</p>}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-800">Quote details</h2>
            <a href={`${API}/public/quote/${token}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-purple-700 font-medium" data-testid="quote-download-pdf"><Download className="w-4 h-4" /> PDF</a>
          </div>
          <p className="text-xs text-slate-500 mb-3">Issued {q.created_at}</p>
          <div className="divide-y text-sm" data-testid="quote-items">
            {q.items.map((it, i) => (
              <div key={i} className="flex justify-between gap-3 py-2"><span className="text-slate-700 whitespace-pre-line">{it.description}<span className="text-slate-400"> × {it.quantity}</span></span><span className="font-medium text-slate-800 whitespace-nowrap">{money(it.amount)}</span></div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t text-sm space-y-1">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{money(q.subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Tax</span><span>{money(q.tax)}</span></div>
            <div className="flex justify-between font-bold text-slate-800 text-base"><span>Total</span><span>{money(q.total)}</span></div>
          </div>
          {q.notes && <p className="mt-4 text-sm text-slate-600 whitespace-pre-line rounded-lg bg-slate-50 p-3" data-testid="quote-notes">{q.notes}</p>}
        </div>

        {q.can_respond && <DecisionForm token={token} quote={{ ...q, company_name: company.name }} onDone={() => setRefresh((r) => r + 1)} />}
        <p className="text-center text-xs text-slate-400 pb-6">Questions? {company.phone ? `Call ${company.phone}` : ""}{company.email ? ` · ${company.email}` : ""}</p>
      </div>
    </div>
  );
}
